import type { Editor } from '@tiptap/core';
import type { Node } from '@tiptap/pm/model';

type LinkAttributes = {
  href: string;
  target?: string | null;
  rel?: string | null;
  class?: string | null;
};

const getLinkMarkAttrs = (node: Node): LinkAttributes | null => {
  return (
    (node.marks?.find((mark) => mark.type.name === 'link')
      ?.attrs as LinkAttributes) ?? null
  );
};

const reduceText = (acc: string, node: Node): string => {
  return node.isText
    ? acc.concat(node.text ?? '')
    : node.content.content.reduce(reduceText, acc);
};

/**
 * Reducer function to get the link attributes associated with a selection's slice of nodes iff all the text
 * nodes have a link mark with the same href attribute. Otherwise, returns null.
 *
 * @remarks The initial value of the reduction is expected to be an empty object. The accumulator will be set to null
 * any time a text node does not have a link mark or the link mark's href does not match the current href, and once null
 * the accumulator will remain null.
 */
const reduceLink = (
  acc: LinkAttributes | null,
  node: Node
): LinkAttributes | null => {
  if (acc === null) {
    return null;
  }

  if (!node.isText) {
    return node.content.content.reduce(reduceLink, acc);
  }

  const attrs = getLinkMarkAttrs(node);

  return attrs ? (!acc.href || attrs.href === acc.href ? attrs : null) : null;
};

export const getLinkText = (editor: Editor) => {
  let text = '';

  editor
    .chain()
    .focus()
    .extendMarkRange('link')
    .command(({ tr }) => {
      const slice = tr.selection.content();
      text = slice.content.content.reduce(reduceText, '');
      return true;
    })
    .run();

  return text;
};

export const getLinkAttributes = (
  editor: Editor,
  opts: { position?: 'start' | 'end' | 'all' | number | boolean | null } = {}
) => {
  let attrs: LinkAttributes | null = {} as LinkAttributes;

  editor
    .chain()
    .focus(opts.position)
    .extendMarkRange('link')
    .command(({ tr }) => {
      const slice = tr.selection.content();
      attrs = slice.content.content.reduce(reduceLink, {} as LinkAttributes);
      return true;
    })
    .run();

  return attrs || {};
};
